import { FC, useEffect, useState } from 'react'
import { UserRegistrationForm } from '../forms/UserRegistrationForm'
import { useUserCtx } from '../hooks/context-hooks'
import { Box, Card, Stack, Typography } from '@mui/material'
import { CenteredSpinner } from '../components/Spinner'
import { ScreenTitle } from '../components/ScreenTitle'
import { useTranslation } from 'react-i18next'
import { invalidateCustomer } from '../api/customer'
import { invalidateLocalityCache } from '../api/localities'
import { useUpdateUser } from '../api/user'
import { toast } from 'react-toastify'
import { UserStatus } from '../enums/user-status'
import { useLocation, useNavigate } from 'react-router-dom'
import { AppRoutesNavigation } from '../app-routes'
import { invalidateWorkAssignmentCache } from 'src/api/work-assignments'
import { invalidateDeviation } from 'src/api/deviation'
import { invalidateScheduledTaskCache } from 'src/api/scheduledTask'
import { invalidateWashingReportCache } from 'src/api/washing-report'
import { invalidateImpregnationCache } from 'src/api/impregnation'
import { invalidateSchemaForm } from 'src/api/form-schema'
import { invalidationActivityLogCache } from 'src/api/activity-log'
import { invalidateMainComponentCache } from 'src/api/main-component'
import { NotificationPreferences } from 'src/components/notifications/NotificationPreferences'
import { useCurrentCustomerId } from 'src/hooks/useCurrentCustomerId'

export const UserProfileScreen: FC = () => {
  const { user, isLoadingUser, refetch } = useUserCtx()
  const currentCustomerId = useCurrentCustomerId()
  const { mutateAsync, isLoading } = useUpdateUser()
  const { state } = useLocation()
  const { t } = useTranslation('user-register')
  const [isUserUpdated, setIsUserUpdated] = useState(false)

  useEffect(() => {
    if (!isLoading && user?.status == UserStatus.Complete && isUserUpdated) {
      setTimeout(() => {
        setIsUserUpdated(false)
        navigate((state as any)?.prevPath ?? AppRoutesNavigation.Home)
      }, 1000)
      return
    }
  }, [isUserUpdated, user])

  const navigate = useNavigate()

  const handleSubmit = async form => {
    mutateAsync(form, {
      onSuccess: () => {
        toast.success(t('successfullyUpdated', { ns: 'common-api-message' }))
        invalidateLocalityCache.getLocalities()
        invalidateLocalityCache.getLocality()
        invalidateCustomer.getCustomerUsers()
        invalidateCustomer.getCustomerUsersForAdmins()
        invalidateWorkAssignmentCache.getWorkAssignments()
        invalidateWorkAssignmentCache.getWorkAssignment()
        invalidateDeviation.getDeviations()
        invalidateScheduledTaskCache.useScheduledTasks()
        invalidateWashingReportCache.getWashingReports()
        invalidateImpregnationCache.getImpregnations()
        invalidateSchemaForm.getFormSchemaReports()
        invalidationActivityLogCache.getCageActivityLogs()
        invalidationActivityLogCache.getMainComponentActivityLogs()
        invalidateMainComponentCache.getMaintenancePerformHistory()
        refetch()
        setIsUserUpdated(true)
      },
      onError: () => {
        toast.error(t('unExpectedError', { ns: 'common-api-message' }))
      },
    })
  }
  return (
    <Box className="flex flex-col items-center pt-6 pb-10">
      <ScreenTitle title={t('profile', { ns: 'user-register' })} />
      <Stack spacing={2} sx={{ width: '100%', maxWidth: 640, px: 2, mt: 2 }}>
        <Card sx={{ p: 3, boxShadow: 'none', border: '1px solid', borderColor: 'divider' }}>
          <Typography variant="h6" fontWeight={600} mb={2}>
            {t('profile', { ns: 'user-register' })}
          </Typography>
          {isLoadingUser ? (
            <CenteredSpinner />
          ) : (
            <UserRegistrationForm
              onSubmit={handleSubmit}
              initialValues={{ ...user }}
              isRegisterForm={false}
              isLoading={isLoading}
              onCancel={() => {
                if (user?.status == UserStatus.InComplete) {
                  toast.error(t('profileUpdateRequired', { ns: 'common-api-message' }))
                  return
                }
                navigate((state as any)?.prevPath ?? AppRoutesNavigation.Home)
              }}
            />
          )}
        </Card>
        {!isLoadingUser && currentCustomerId > 0 && <NotificationPreferences customerId={currentCustomerId} />}
      </Stack>
    </Box>
  )
}
